import { FC, useCallback, useMemo } from "react";
import {
  CreatePolicyOnDocumentPayload,
  DocumentStages,
  DocumentStatus,
  GetCreatePolicyOnDocumentFormResponseDocument,
  GetEditPolicyOnDocumentFormResponseDocument,
  Services,
  TransactionalEmailEvents,
} from "../../../../../graphql/__GENERATED__/types";
import useDocumentDetails from "../../../../../hooks/useDocumentDetails";
import { useUIStore } from "../../../../../hooks/useUIStore";
import UploadIcon from "../../../../../components/Icons/uploadIcon";
import EditableSection from "../../../../../components/EditableSection";
import { formatISODate } from "../../../../../utils/formatNumber";
import useConfirmedAction from "../../../../../hooks/useConfirmedAction";
import Typography from "../../../../../components/Typography";
import useDocumentOrder from "../../../../../hooks/useDocumentOrder";
import { DetailsRowProps } from "../../../../../components/Details/components/Main/Grid/DetailsRow";
import { DetailsRowValueVariants } from "../../../../../components/Details/components/Main/Grid/DetailsRowValue";
import DetailsSectionColumns from "../../../../../components/Details/components/Main/Grid/DetailsSectionColumns";
import { IconNames } from "../../../../../components/Icons/styles/iconNames";

const PolicyData: FC = () => {
  const { documentColor } = useUIStore();
  const {
    documentData: { policy, _id, stage, status },
    refetch,
    handleSendEmail,
    handleSetDocumentsAsSent,
  } = useDocumentDetails();

  const handleSendPolicyEmail = useConfirmedAction(
    "Deseja enviar a apólice para o produtor?",
    () => handleSendEmail(TransactionalEmailEvents.UploadedPolicy)
  );

  const footerButtons = useMemo(() => {
    return policy && status === DocumentStatus.Done
      ? [
          {
            text: "Enviar",
            onClick: handleSendPolicyEmail,
            icon: <UploadIcon color={documentColor} />,
          },
          {
            text: "Marcar como enviada",
            onClick: handleSetDocumentsAsSent,
            iconName: IconNames.Sent,
          },
        ]
      : [];
  }, [policy, documentColor]);

  useCallback(async () => {
    refetch();
    handleSendPolicyEmail();
  }, [handleSendEmail]);

  const policyInfo: DetailsRowProps[][] = useMemo(() => {
    return [
      [
        {
          label: "número",
          value: policy?.externalId,
        },
        {
          label: "vigência",
          value: policy
            ? `${formatISODate(policy.startDate)} - ${formatISODate(
                policy.endDate
              )}`
            : "-",
        },
        {
          label: "Arquivo",
          variant: DetailsRowValueVariants.File,
          value: {
            file: policy?.policyFile,
            objectId: policy?._id,
            fieldPath: "policyFile",
            serviceName: Services.Policies,
          },
        },
      ],
    ];
  }, [policy]);

  const currentStageStatus = useDocumentOrder(stage!, status!);
  const effectivePolicy = useDocumentOrder(
    DocumentStages.Policy,
    DocumentStatus.Effective
  );

  const editQuery = useMemo(
    () =>
      currentStageStatus < effectivePolicy
        ? GetEditPolicyOnDocumentFormResponseDocument
        : undefined,
    [currentStageStatus]
  );

  return (
    <EditableSection<CreatePolicyOnDocumentPayload, {}>
      title="Apólice"
      noTitle
      main
      createPayload={{ documentId: _id! }}
      createQuery={GetCreatePolicyOnDocumentFormResponseDocument}
      editQuery={editQuery}
      objectId={policy?._id}
      footerButtons={footerButtons}
      addText={"Adicionar Apólice"}
      onSubmit={() => {
        refetch(), handleSendPolicyEmail();
      }}
    >
      {policy ? (
        <DetailsSectionColumns columns={policyInfo} />
      ) : (
        <Typography variant="textMedium" uppercase color={documentColor}>
          Não há apólice adicionada
        </Typography>
      )}
    </EditableSection>
  );
};

export default PolicyData;
